import { useMemo } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  PointhomeFunnelDetailRetentionData,
} from '../../../../types/pointhomeFunnel.types';

type Props = {
  detailRetentionDay: number;
  onChangeDetailRetentionDay: (day: number) => void;
  detailRetentionData: PointhomeFunnelDetailRetentionData;
  detailLoading: boolean;
};

export default function PointhomeDetailRetentionDashboard({
  detailRetentionDay,
  onChangeDetailRetentionDay,
  detailRetentionData,
  detailLoading,
}: Props) {
  const DAY_OPTIONS = Array.from({ length: 7 }, (_, idx) => idx + 1);

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat('ko-KR'),
    []
  );

  const {
    totalNewUser,
    totalRetention,
    totalCashRetention,
    totalCashSum,
    avgRetentionRate,
    chartData,
    sortedRows,
  } =
    useMemo(() => {
      if (!detailRetentionData || detailRetentionData.length === 0) {
        return {
          totalNewUser: 0,
          totalRetention: 0,
          totalCashRetention: 0,
          totalCashSum: 0,
          avgRetentionRate: 0,
          chartData: [] as {
            label: string;
            newUser: number;
            retained: number;
          }[],
          sortedRows: [] as PointhomeFunnelDetailRetentionData,
        };
      }

      let sumNewUser = 0;
      let sumRetention = 0;
      let sumCashRetention = 0;
      let sumCashSum = 0;

      const sorted = [...detailRetentionData].sort((a, b) =>
        dayjs(a.date).diff(dayjs(b.date))
      );

      const chartData = sorted.map((row) => {
        const newUser = row.newUserCount ?? 0;
        const retained = row.retention ?? 0;
        const cashRet = row.cashRetention ?? 0;
        const cashSum = row.cashSum ?? 0;

        sumNewUser += newUser;
        sumRetention += retained;
        sumCashRetention += cashRet;
        sumCashSum += cashSum;

        return {
          label: dayjs(row.date).format('MM/DD'),
          newUser,
          retained,
        };
      });

      const rate =
        sumNewUser > 0 ? Math.min(100, (sumRetention / sumNewUser) * 100) : 0;

      return {
        totalNewUser: sumNewUser,
        totalRetention: sumRetention,
        totalCashRetention: sumCashRetention,
        totalCashSum: sumCashSum,
        avgRetentionRate: rate,
        chartData,
        sortedRows: sorted,
      };
    }, [detailRetentionData]);

  return (
    <Box sx={{ mt: 2 }}>
      {/* 리텐션 일자 드롭다운 */}
      <FormControl size="small" sx={{ minWidth: 140, mb: 2 }}>
        <InputLabel id="pointhome-retention-day-label">리텐션 일자</InputLabel>
        <Select
          labelId="pointhome-retention-day-label"
          value={detailRetentionDay}
          label="리텐션 일자"
          onChange={(event) =>
            onChangeDetailRetentionDay(Number(event.target.value))
          }
        >
          {DAY_OPTIONS.map((day) => (
            <MenuItem key={day} value={day}>
              {`D${day}`}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* 상단 요약 배지 영역 */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ mt: 1, mb: 3, flexWrap: 'wrap' }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2.5,
            py: 1.5,
            borderRadius: 999,
            background:
              'linear-gradient(90deg, #16a34a 0%, #22c55e 100%)',
            color: '#ffffff',
            minWidth: 240,
            justifyContent: 'space-between',
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            신규 사용자
          </Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 800 }}>
            {numberFormatter.format(totalNewUser)}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2.5,
            py: 1.5,
            borderRadius: 999,
            background:
              'linear-gradient(90deg, #ec4899 0%, #f97316 100%)',
            color: '#ffffff',
            minWidth: 240,
            justifyContent: 'space-between',
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            D{detailRetentionDay} 유저 수
          </Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 800 }}>
            {numberFormatter.format(totalRetention)}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2.5,
            py: 1.5,
            borderRadius: 999,
            backgroundColor: '#374151',
            color: '#ffffff',
            minWidth: 240,
            justifyContent: 'space-between',
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            보상 잔존율
          </Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 800 }}>
            {avgRetentionRate.toFixed(1)}%
          </Typography>
        </Box>
      </Stack>

      {/* 에어리어 차트 영역 */}
      <Box
        sx={{
          width: '100%',
          height: 260,
          backgroundColor: '#ffffff',
        }}
      >
        {detailLoading ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              상세 리텐션 데이터를 불러오는 중입니다...
            </Typography>
          </Box>
        ) : chartData.length === 0 ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              데이터가 없습니다.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              backgroundColor: '#ffffff',
              border: '1px solid #e0e0e0',
              width: '100%',
              height: 260,
            }}
          >
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart
                data={chartData}
                margin={{ top: 20, right: 24, left: 4, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="label"
                  tick={{ fill: '#6b7280' }}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <YAxis
                  tick={{ fill: '#6b7280' }}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <RechartsTooltip
                  formatter={(value: number, name: string) => {
                    if (name === '신규 사용자') {
                      return [numberFormatter.format(value), '신규 사용자'];
                    }
                    if (name === '유저 수') {
                      return [numberFormatter.format(value), '유저 수'];
                    }
                    return [value, name];
                  }}
                  labelFormatter={(label) => `${label}`}
                />
                <Area
                  type="monotone"
                  dataKey="newUser"
                  name="신규 사용자"
                  stroke="#16a34a"
                  strokeWidth={2.5}
                  fill="#bbf7d0"
                  fillOpacity={0.6}
                  activeDot={{ r: 5 }}
                />
                <Area
                  type="monotone"
                  dataKey="retained"
                  name="유저 수"
                  stroke="#ec4899"
                  strokeWidth={2.5}
                  fill="#fbcfe8"
                  fillOpacity={0.7}
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Box>

      {/* 상세 테이블 영역 */}
      <Box sx={{ mt: 3 }}>
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: 'none',
            border: '1px solid #e5e7eb',
            borderRadius: 2,
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>날짜</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  DAU
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  신규 사용자
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {`D${detailRetentionDay} 유저 수`}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {`D${detailRetentionDay} 캐시 적립 유저 수`}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {`D${detailRetentionDay} 접속 잔존율`}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {`D${detailRetentionDay} 보상 잔존율`}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {`D${detailRetentionDay} 평균 캐시 적립 원`}
                </TableCell>
              </TableRow>
              {/* 전체 요약 행 */}
              <TableRow sx={{ backgroundColor: '#f3f4f6' }}>
                <TableCell sx={{ fontWeight: 700 }}>전체</TableCell>
                <TableCell align="right">
                  {numberFormatter.format(
                    sortedRows.reduce((sum, row) => sum + (row.dau ?? 0), 0)
                  )}
                </TableCell>
                <TableCell align="right">
                  {numberFormatter.format(totalNewUser)}
                </TableCell>
                <TableCell align="right">
                  {numberFormatter.format(totalRetention)}
                </TableCell>
                <TableCell align="right">
                  {numberFormatter.format(totalCashRetention)}
                </TableCell>
                <TableCell align="right">
                  {totalNewUser > 0
                    ? `${(
                        (totalCashRetention / totalNewUser) *
                        100
                      ).toFixed(1)}%`
                    : '-'}
                </TableCell>
                <TableCell align="right">
                  {totalNewUser > 0
                    ? `${(
                        (totalCashRetention / totalNewUser) *
                        100
                      ).toFixed(1)}%`
                    : '-'}
                </TableCell>
                <TableCell align="right">
                  {totalCashRetention > 0
                    ? numberFormatter.format(
                        Math.round((totalCashSum / 6) / totalCashRetention)
                      )
                    : '-'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((row) => {
                const dau = row.dau ?? 0;
                const newUser = row.newUserCount ?? 0;
                const retained = row.retention ?? 0;
                const cashRet = row.cashRetention ?? 0;
                const cashSum = row.cashSum ?? 0;

                const accessRate =
                  newUser > 0 ? (retained / newUser) * 100 : 0;
                const cashRate =
                  newUser > 0 ? (cashRet / newUser) * 100 : 0;
                const avgCashPerUser =
                  cashRet > 0 ? Math.round((cashSum / 6) / cashRet) : 0;

                return (
                  <TableRow key={row.date}>
                    <TableCell>{row.date}</TableCell>
                    <TableCell align="right">
                      {numberFormatter.format(dau)}
                    </TableCell>
                    <TableCell align="right">
                      {numberFormatter.format(newUser)}
                    </TableCell>
                    <TableCell align="right">
                      {numberFormatter.format(retained)}
                    </TableCell>
                    <TableCell align="right">
                      {numberFormatter.format(cashRet)}
                    </TableCell>
                    <TableCell align="right">
                      {newUser > 0 ? `${accessRate.toFixed(1)}%` : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {newUser > 0 ? `${cashRate.toFixed(1)}%` : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {avgCashPerUser > 0
                        ? numberFormatter.format(avgCashPerUser)
                        : '-'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}

